import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Text,
    Button,
    useColorModeValue,
    SimpleGrid,
    useToast,
    Spinner,
    Center,
    VStack,
    HStack,
    Badge,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Avatar,
    Tag,
    TagLabel,
    Grid,
    GridItem,
    Stack,
    Progress,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
} from '@chakra-ui/react';
import {
    MdFitnessCenter,
    MdPerson,
    MdCalendarToday,
    MdTimelapse,
    MdInfo,
    MdFlag,
    MdPlayArrow,
    MdRepeat,
    MdFitnessCenter as MdExercise,
    MdAccessTime,

    MdRestaurantMenu,
    MdDirectionsWalk,
    MdTrendingUp,
    MdMoreHoriz,
} from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import Icon from 'components/icons/Icon';
import { newWorkoutService, WorkoutPlan, DailyWorkout, DailyWorkoutExercise } from 'services/newWorkoutService';
import { useAuth } from 'contexts/AuthContext';

const NewWorkoutDetails: React.FC = () => {
    const [workout, setWorkout] = useState<WorkoutPlan | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedWeek, setSelectedWeek] = useState<number>(1);
    const [showAllExercises, setShowAllExercises] = useState<{ [key: string]: boolean }>({});

    const { workoutId } = useParams<{ workoutId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();

    const textColor = useColorModeValue('gray.700', 'white');
    const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const bgGradient = useColorModeValue('linear(to-br, blue.50, purple.50)', 'linear(to-br, gray.800, gray.900)');

    // Calculate dynamic workout plan properties
    const getWorkoutPlanInfo = () => {
        if (!workout) return { totalWeeks: 0, daysPerWeek: 7, totalDays: 0 };

        const maxDayNumber = Math.max(...workout.daily_workouts.map(dw => dw.day_number));
        const totalDays = maxDayNumber;
        const daysPerWeek = 7; // Standard week length
        const totalWeeks = Math.ceil(totalDays / daysPerWeek);

        return { totalWeeks, daysPerWeek, totalDays };
    };

    const { totalWeeks, daysPerWeek, totalDays } = getWorkoutPlanInfo();

    // Get the appropriate back navigation path based on user role
    const getBackPath = () => {
        switch (user?.role) {
            case 'admin':
                return '/admin/default';
            case 'agency':
                return '/agency/default';
            case 'trainer':
                return '/trainer/my-clients';
            default:
                return '/admin/default';
        }
    };

    useEffect(() => {
        if (workoutId) {
            fetchWorkoutDetails();
        }
    }, [workoutId]);

    // Reset selected week when workout data changes
    useEffect(() => {
        if (workout && totalWeeks > 0) {
            // If selected week is out of bounds, reset to 1
            if (selectedWeek > totalWeeks) {
                setSelectedWeek(1);
            } else {
                // If the current selected week has no workout days, find the first week that does
                if (!weekHasDays(selectedWeek)) {
                    for (let week = 1; week <= totalWeeks; week++) {
                        if (weekHasDays(week)) {
                            setSelectedWeek(week);
                            break;
                        }
                    }
                }
            }
        }
    }, [workout, totalWeeks, selectedWeek]);

    const fetchWorkoutDetails = async () => {
        if (!workoutId) return;

        try {
            setLoading(true);
            const workoutPlan = await newWorkoutService.getWorkoutPlan(workoutId);
            setWorkout(workoutPlan);
        } catch (error) {
            console.error('Error fetching workout details:', error);
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to load workout details',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty?.toLowerCase()) {
            case 'beginner':
                return 'green';
            case 'intermediate':
                return 'yellow';
            case 'advanced':
                return 'red';
            default:
                return 'gray';
        }
    };

    const getPlanTypeColor = (planType: string) => {
        switch (planType?.toLowerCase()) {
            case 'strength':
                return 'purple';
            case 'cardio':
                return 'red';
            case 'mixed':
                return 'blue';
            case 'weight_loss':
                return 'orange';
            case 'muscle_gain':
                return 'green';
            default:
                return 'gray';
        }
    };

    const getWeekDays = (week: number) => {
        if (!workout) return [];
        const startDay = (week - 1) * daysPerWeek + 1;
        const endDay = week * daysPerWeek;
        return workout.daily_workouts.filter(
            (dw) => dw.day_number >= startDay && dw.day_number <= endDay
        ).sort((a, b) => a.day_number - b.day_number);
    };

    // Helper function to check if a week has any workout days
    const weekHasDays = (week: number) => {
        return getWeekDays(week).length > 0;
    };

    // Helper function to get the date range for a week
    const getWeekDateRange = (week: number) => {
        const weekDays = getWeekDays(week);
        if (weekDays.length === 0) return null;

        const datesWithData = weekDays
            .filter(dw => dw.workout_date)
            .map(dw => new Date(dw.workout_date))
            .sort((a, b) => a.getTime() - b.getTime());

        if (datesWithData.length === 0) return null;

        const startDate = datesWithData[0];
        const endDate = datesWithData[datesWithData.length - 1];

        const formatShort = (date: Date) => date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });

        if (startDate.getTime() === endDate.getTime()) {
            return formatShort(startDate);
        } else {
            return `${formatShort(startDate)} - ${formatShort(endDate)}`;
        }
    };

    // Toggle function for showing all exercises
    const toggleShowAllExercises = (dailyWorkoutId: string) => {
        setShowAllExercises(prev => ({
            ...prev,
            [dailyWorkoutId]: !prev[dailyWorkoutId]
        }));
    };

    const DailyWorkoutCard: React.FC<{ dailyWorkout: DailyWorkout }> = ({ dailyWorkout }) => {
        const isRestDay = dailyWorkout.is_rest_day;
        const isExpanded = showAllExercises[dailyWorkout.id] || false;
        const exercisePreviewLimit = 3;
        const [expandedExercises, setExpandedExercises] = useState<{ [key: string]: boolean }>({});

        const toggleExerciseExpanded = (exerciseId: string) => {
            setExpandedExercises(prev => ({
                ...prev,
                [exerciseId]: !prev[exerciseId]
            }));
        };

        return (
            <Card
                p={5}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                opacity={isRestDay ? 0.7 : 1}
                bgGradient={isRestDay ? undefined : bgGradient}
            >
                <VStack align="stretch" spacing={4}>
                    {/* Day Header */}
                    <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                            <HStack>
                                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                                    Day {dailyWorkout.day_number}
                                </Text>
                                <Badge colorScheme={isRestDay ? 'gray' : 'blue'} variant="subtle">
                                    {dailyWorkout.day_of_week}
                                </Badge>
                            </HStack>
                            {dailyWorkout.workout_date ? (
                                <Text fontSize="sm" color={textColorSecondary} fontWeight="medium">
                                    📅 {new Date(dailyWorkout.workout_date).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </Text>
                            ) : (
                                <Text fontSize="sm" color={textColorSecondary} fontStyle="italic">
                                    Schedule not set
                                </Text>
                            )}
                            <Text fontSize="md" fontWeight="medium" color={textColor}>
                                {dailyWorkout.title}
                            </Text>
                        </VStack>
                        {isRestDay ? (
                            <Icon as={MdRestaurantMenu} color="gray.500" w="24px" h="24px" />
                        ) : (
                            <Icon as={MdFitnessCenter} color="blue.500" w="24px" h="24px" />
                        )}
                    </Flex>

                    {!isRestDay && (
                        <>
                            <Divider />

                            {/* Workout Stats */}
                            <SimpleGrid columns={2} spacing={4}>
                                <Stat size="sm">
                                    <StatLabel fontSize="xs" color={textColorSecondary}>Exercises</StatLabel>
                                    <StatNumber fontSize="lg" color={textColor}>
                                        {dailyWorkout.exercises.length}
                                    </StatNumber>
                                </Stat>
                                <Stat size="sm">
                                    <StatLabel fontSize="xs" color={textColorSecondary}>Duration</StatLabel>
                                    <StatNumber fontSize="lg" color={textColor}>
                                        {dailyWorkout.estimated_duration}m
                                    </StatNumber>
                                </Stat>
                            </SimpleGrid>

                            {/* Exercise List */}
                            {dailyWorkout.exercises.length > 0 && (
                                <>
                                    <Divider />
                                    <VStack align="stretch" spacing={3}>
                                        {/* Header */}
                                        <HStack justify="space-between" align="center">
                                            <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                                                Exercises ({dailyWorkout.exercises.length})
                                            </Text>
                                            {dailyWorkout.exercises.length > exercisePreviewLimit && (
                                                <Button
                                                    size="xs"
                                                    variant="outline"
                                                    colorScheme="blue"
                                                    onClick={() => toggleShowAllExercises(dailyWorkout.id)}
                                                >
                                                    {isExpanded ? 'Show Less' : `Show All`}
                                                </Button>
                                            )}
                                        </HStack>

                                        {/* Exercise Items */}
                                        <VStack align="stretch" spacing={2}>
                                            {(isExpanded
                                                ? dailyWorkout.exercises
                                                : dailyWorkout.exercises.slice(0, exercisePreviewLimit)
                                            ).map((exercise, index) => {
                                                const isExerciseExpanded = expandedExercises[exercise.id] || false;
                                                const hasAdditionalInfo = exercise.notes || exercise.exercise?.description;

                                                return (
                                                    <Box
                                                        key={exercise.id}
                                                        p={3}
                                                        bg={useColorModeValue('gray.50', 'gray.700')}
                                                        borderRadius="md"
                                                        borderLeft="3px solid"
                                                        borderLeftColor="blue.400"
                                                    >
                                                        <VStack align="stretch" spacing={2}>
                                                            {/* Exercise Name & Dropdown Arrow */}
                                                            <HStack justify="space-between" align="center">
                                                                <HStack spacing={2} flex="1">
                                                                    <Text fontSize="sm" color="blue.500" fontWeight="bold" minW="20px">
                                                                        {index + 1}.
                                                                    </Text>
                                                                    <VStack align="start" spacing={0} flex="1">
                                                                        <Text fontSize="sm" color={textColor} fontWeight="medium">
                                                                            {exercise.exercise?.name || 'Unknown Exercise'}
                                                                        </Text>
                                                                        {exercise.exercise?.muscle_group && (
                                                                            <Text fontSize="xs" color={textColorSecondary}>
                                                                                {exercise.exercise.muscle_group}
                                                                            </Text>
                                                                        )}
                                                                    </VStack>
                                                                </HStack>
                                                                <HStack spacing={2}>
                                                                    {/* Badges */}
                                                                    {exercise.is_warmup && (
                                                                        <Badge colorScheme="orange" size="sm">Warmup</Badge>
                                                                    )}
                                                                    {exercise.is_cooldown && (
                                                                        <Badge colorScheme="blue" size="sm">Cooldown</Badge>
                                                                    )}
                                                                    {exercise.is_superset && (
                                                                        <Badge colorScheme="purple" size="sm">Superset</Badge>
                                                                    )}

                                                                    {/* Dropdown Arrow - only show if there's additional info */}
                                                                    {hasAdditionalInfo && (
                                                                        <Button
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            onClick={() => toggleExerciseExpanded(exercise.id)}
                                                                            p={1}
                                                                            minW="auto"
                                                                            h="auto"
                                                                        >
                                                                            <Icon
                                                                                as={isExerciseExpanded ? MdTrendingUp : MdDirectionsWalk}
                                                                                w="12px"
                                                                                h="12px"
                                                                                color={textColorSecondary}
                                                                                transform={isExerciseExpanded ? "rotate(180deg)" : "none"}
                                                                                transition="transform 0.2s"
                                                                            />
                                                                        </Button>
                                                                    )}
                                                                </HStack>
                                                            </HStack>

                                                            {/* Exercise Details */}
                                                            <HStack spacing={4} fontSize="xs">
                                                                <Text color={textColor}>
                                                                    <Text as="span" color={textColorSecondary} fontWeight="medium">Sets:</Text> {exercise.sets}
                                                                </Text>
                                                                <Text color={textColor}>
                                                                    <Text as="span" color={textColorSecondary} fontWeight="medium">Reps:</Text> {exercise.reps}
                                                                </Text>
                                                                {exercise.weight && (
                                                                    <Text color={textColor}>
                                                                        <Text as="span" color={textColorSecondary} fontWeight="medium">Weight:</Text> {exercise.weight}
                                                                    </Text>
                                                                )}
                                                                {exercise.rest_time && (
                                                                    <Text color={textColor}>
                                                                        <Text as="span" color={textColorSecondary} fontWeight="medium">Rest:</Text> {exercise.rest_time}
                                                                    </Text>
                                                                )}
                                                            </HStack>

                                                            {/* Expanded Content - Notes and Instructions */}
                                                            {isExerciseExpanded && hasAdditionalInfo && (
                                                                <VStack align="stretch" spacing={2} pt={2} mt={2} borderTop="1px solid" borderTopColor={useColorModeValue('gray.200', 'gray.600')}>
                                                                    {exercise.notes && (
                                                                        <Box>
                                                                            <HStack align="start" spacing={2} mb={1}>
                                                                                <Text fontSize="xs" color="orange.500">💡</Text>
                                                                                <Text fontSize="xs" color={textColorSecondary} fontWeight="bold">
                                                                                    Trainer Notes:
                                                                                </Text>
                                                                            </HStack>
                                                                            <Text fontSize="xs" color={textColor} pl={4}>
                                                                                {exercise.notes}
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                    {exercise.exercise?.description && (
                                                                        <Box>
                                                                            <HStack align="start" spacing={2} mb={1}>
                                                                                <Text fontSize="xs" color="blue.500">📝</Text>
                                                                                <Text fontSize="xs" color={textColorSecondary} fontWeight="bold">
                                                                                    Instructions:
                                                                                </Text>
                                                                            </HStack>
                                                                            <Text fontSize="xs" color={textColor} pl={4} lineHeight="tall">
                                                                                {exercise.exercise.description}
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                </VStack>
                                                            )}
                                                        </VStack>
                                                    </Box>
                                                );
                                            })}
                                        </VStack>

                                        {/* Show more indicator */}
                                        {!isExpanded && dailyWorkout.exercises.length > exercisePreviewLimit && (
                                            <Text fontSize="xs" color={textColorSecondary} textAlign="center" fontStyle="italic">
                                                ... and {dailyWorkout.exercises.length - exercisePreviewLimit} more exercise{dailyWorkout.exercises.length - exercisePreviewLimit !== 1 ? 's' : ''}
                                            </Text>
                                        )}
                                    </VStack>
                                </>
                            )}
                        </>
                    )}

                    {/* Description */}
                    {dailyWorkout.description && (
                        <>
                            <Divider />
                            <Text fontSize="sm" color={textColorSecondary}>
                                {dailyWorkout.description}
                            </Text>
                        </>
                    )}

                    {/* Notes */}
                    {dailyWorkout.notes && (
                        <>
                            <Divider />
                            <VStack align="start" spacing={1}>
                                <Text fontSize="xs" color={textColorSecondary} fontWeight="medium">
                                    Notes:
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                    {dailyWorkout.notes}
                                </Text>
                            </VStack>
                        </>
                    )}
                </VStack>
            </Card>
        );
    };

    if (loading) {
        return (
            <Center h="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text color={textColorSecondary}>Loading workout details...</Text>
                </VStack>
            </Center>
        );
    }

    if (!workout) {
        return (
            <Center h="400px">
                <VStack spacing={4}>
                    <Icon as={MdFitnessCenter} w="64px" h="64px" color="gray.400" />
                    <Text fontSize="lg" color={textColorSecondary}>
                        Workout plan not found
                    </Text>
                    <Button onClick={() => navigate(getBackPath())} colorScheme="blue" variant="outline">
                        Back to Workouts
                    </Button>
                </VStack>
            </Center>
        );
    }

    const totalExercises = workout.daily_workouts.reduce(
        (total, dw) => total + dw.exercises.length,
        0
    );
    const activeDays = workout.daily_workouts.filter((dw) => !dw.is_rest_day).length;
    const totalDuration = workout.daily_workouts.reduce(
        (total, dw) => total + dw.estimated_duration,
        0
    );

    return (
        <Box p={6}>
            {/* Back Button */}
            <Button
                onClick={() => navigate(getBackPath())}
                variant="ghost"
                leftIcon={<Icon as={MdPlayArrow} transform="rotate(180deg)" />}
                mb={6}
                color={textColorSecondary}
            >
                Back to Workouts
            </Button>

            {/* Header */}
            <Card p={6} mb={6} bgGradient={bgGradient}>
                <VStack align="stretch" spacing={6}>
                    {/* Title and Badges */}
                    <Flex justify="space-between" align="start" wrap="wrap" gap={4}>
                        <VStack align="start" spacing={2} flex="1">
                            <HStack>
                                <Text fontSize="3xl" fontWeight="bold" color={textColor}>
                                    {workout.title}
                                </Text>

                            </HStack>
                            <Text fontSize="lg" color={textColorSecondary}>
                                {workout.description}
                            </Text>
                        </VStack>

                        <HStack spacing={3}>
                            <Badge colorScheme={getDifficultyColor(workout.difficulty)} size="lg" px={3} py={1}>
                                {workout.difficulty}
                            </Badge>
                            {workout.plan_type && (
                                <Badge colorScheme={getPlanTypeColor(workout.plan_type)} variant="outline" size="lg" px={3} py={1}>
                                    {workout.plan_type.replace('_', ' ')}
                                </Badge>
                            )}
                            <Badge colorScheme={workout.is_active ? 'green' : 'gray'} size="lg" px={3} py={1}>
                                {workout.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                        </HStack>
                    </Flex>

                    {/* Overview Stats */}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                        <Stat textAlign="center">
                            <StatLabel color={textColorSecondary}>Total Exercises</StatLabel>
                            <StatNumber color={textColor}>{totalExercises}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                            <StatLabel color={textColorSecondary}>Workout Days</StatLabel>
                            <StatNumber color={textColor}>{activeDays} / {totalDays}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                            <StatLabel color={textColorSecondary}>Total Duration</StatLabel>
                            <StatNumber color={textColor}>{Math.round(totalDuration / 60)}h {totalDuration % 60}m</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                            <StatLabel color={textColorSecondary}>Plan Length</StatLabel>
                            <StatNumber color={textColor}>
                                {totalWeeks} Week{totalWeeks !== 1 ? 's' : ''} ({totalDays} Days)
                            </StatNumber>
                        </Stat>
                    </SimpleGrid>

                    {/* Client and Trainer Info */}
                    <Divider />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <VStack align="start" spacing={3}>
                            <Text fontSize="md" fontWeight="bold" color={textColor}>
                                Client Information
                            </Text>
                            <HStack>
                                <Avatar size="sm" name={workout.client?.name} />
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                        {workout.client?.name || 'N/A'}
                                    </Text>
                                    <Text fontSize="xs" color={textColorSecondary}>
                                        {workout.client?.email || 'N/A'}
                                    </Text>
                                </VStack>
                            </HStack>
                        </VStack>

                        <VStack align="start" spacing={3}>
                            <Text fontSize="md" fontWeight="bold" color={textColor}>
                                Trainer Information
                            </Text>
                            <HStack>
                                <Avatar size="sm" name={workout.trainer?.name} />
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                        {workout.trainer?.name || 'N/A'}
                                    </Text>
                                    <Text fontSize="xs" color={textColorSecondary}>
                                        {workout.trainer?.email || 'N/A'}
                                    </Text>
                                    {workout.trainer?.specialization && (
                                        <Text fontSize="xs" color={textColorSecondary}>
                                            {workout.trainer.specialization}
                                        </Text>
                                    )}
                                </VStack>
                            </HStack>
                        </VStack>
                    </SimpleGrid>

                    {/* Goals */}
                    {workout.goals && (
                        <>
                            <Divider />
                            <VStack align="start" spacing={2}>
                                <Text fontSize="md" fontWeight="bold" color={textColor}>
                                    Goals & Objectives
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                    {workout.goals}
                                </Text>
                            </VStack>
                        </>
                    )}
                </VStack>
            </Card>

            {/* Week Navigation */}
            {totalWeeks > 1 && (
                <Card p={4} mb={6}>
                    <HStack justify="center" spacing={4} wrap="wrap">
                        {Array.from({ length: totalWeeks }, (_, index) => {
                            const weekNumber = index + 1;
                            const startDay = (weekNumber - 1) * daysPerWeek + 1;
                            const endDay = Math.min(weekNumber * daysPerWeek, totalDays);
                            const hasWorkoutDays = weekHasDays(weekNumber);
                            const dateRange = getWeekDateRange(weekNumber);

                            return (
                                <Button
                                    key={weekNumber}
                                    colorScheme={selectedWeek === weekNumber ? 'blue' : 'gray'}
                                    variant={selectedWeek === weekNumber ? 'solid' : 'outline'}
                                    onClick={() => setSelectedWeek(weekNumber)}
                                    size="md"
                                    isDisabled={!hasWorkoutDays}
                                    opacity={!hasWorkoutDays ? 0.5 : 1}
                                    height="auto"
                                    py={3}
                                >
                                    <VStack spacing={1}>
                                        <Text fontSize="sm" fontWeight="bold">
                                            Week {weekNumber}
                                        </Text>
                                        <Text fontSize="xs">
                                            Days {startDay}-{endDay}
                                        </Text>
                                        {dateRange && (
                                            <Text fontSize="xs" opacity={0.8}>
                                                {dateRange}
                                            </Text>
                                        )}
                                        {!hasWorkoutDays && (
                                            <Text fontSize="xs" opacity={0.6}>
                                                No workouts
                                            </Text>
                                        )}
                                    </VStack>
                                </Button>
                            );
                        })}
                    </HStack>
                </Card>
            )}

            {/* Daily Workouts Grid */}
            {getWeekDays(selectedWeek).length > 0 ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                    {getWeekDays(selectedWeek).map((dailyWorkout) => (
                        <DailyWorkoutCard key={dailyWorkout.id} dailyWorkout={dailyWorkout} />
                    ))}
                </SimpleGrid>
            ) : (
                <Center py={12}>
                    <VStack spacing={4}>
                        <Icon as={MdCalendarToday} w="64px" h="64px" color="gray.400" />
                        <Text fontSize="lg" color={textColorSecondary} textAlign="center">
                            No workout days scheduled for Week {selectedWeek}
                        </Text>
                        <Text fontSize="sm" color={textColorSecondary} textAlign="center">
                            This week might be a rest period or not yet planned.
                        </Text>
                    </VStack>
                </Center>
            )}

            {/* Notes Section */}
            {workout.notes && (
                <Card p={6} mt={6}>
                    <VStack align="start" spacing={4}>
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                            Additional Notes
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                            {workout.notes}
                        </Text>
                    </VStack>
                </Card>
            )}


        </Box>
    );
};

export default NewWorkoutDetails;
